/**
 * 인증 스토어 + 계정 API (§26 · Part 2).
 *
 * 토큰은 sessionStorage 에 둔다. localStorage 는 브라우저를 닫아도 남아,
 * 공용 PC 에서 다음 사람이 권한을 그대로 물려받는다.
 *
 * 스토어가 바뀔 때마다 window 이벤트 'aitex:auth' 를 쏜다. 화면(AuthContext)은
 * 이 이벤트로 갱신되므로, 어떤 요청에서 401 → 재발급 실패로 세션이 끊겨도
 * 상단바가 "로그인됨" 으로 남는 일이 없다.
 */
import { getMode, probe } from './client';
/** 모의 인증 백엔드(authMock.ts 13KB)는 모의 모드에서만 지연 로딩한다 (리뷰 5회차 m25). */
const loadAuthMock = () => import('./authMock');

const BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');
export const API = '/api/v1';

export type Role = 'viewer' | 'operator' | 'admin';
export type UserStatus = 'pending' | 'active' | 'suspended' | 'withdrawn';

// 역할 명칭(ROLE_KO)은 components/RoleBadge.tsx 가 단일 출처다 (리뷰 5회차 m21).
export const STATUS_KO: Record<UserStatus, string> = {
  pending: '승인 대기', active: '활성', suspended: '정지', withdrawn: '탈퇴',
};

/** 로그인 응답 user / 스토어 보관 형태. /auth/me 로 갱신되면 선택 필드가 채워진다. */
export interface AuthUser {
  user_id: string;
  display_name: string;
  role: Role;
  status: UserStatus;
  must_change_password: boolean;
  email?: string | null;
  organization?: string | null;
  phone?: string | null;
  created_at?: string | null;
  last_login_at?: string | null;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: 'bearer';
  role: Role;
  user: AuthUser;
}

/**
 * 가입 신청 (R1-5, 2026-10-05). **이메일이 아이디다** — user_id 는 보내지 않고 서버가 이메일로 만든다.
 * 응답은 202 `{user: {...}, status: "pending"}`. 이전 서버는 message 도 주었으므로 둘 다 받는다.
 */
export interface RegisterInput {
  email: string;
  password: string;
  display_name: string;
  organization?: string;
  phone?: string;
  purpose?: string;
  /** 직무 (R3-11) — 고르지 않아도 된다 */
  job_function?: 'designer' | 'md' | 'sales' | 'other';
}

export interface RegisterResult {
  status: string;
  user?: Partial<AuthUser>;
  message?: string;
}

/**
 * 이메일 중복 확인 결과. `available: null` 은 "지금은 확인할 수 없음" —
 * 서버에 엔드포인트가 아직 없거나(404) 네트워크 오류. 화면은 제출 시 확인으로 물러난다.
 */
export type EmailCheck =
  | { available: true }
  | { available: false; reason: 'taken' | 'invalid' | 'withdrawn' }
  | { available: null; reason: 'unsupported' | 'error' };

/**
 * API 오류. status + 서버 error 코드(VALIDATION_ERROR · ACCOUNT_PENDING …)를 함께 든다.
 * 화면은 코드로 분기하고(승인 대기는 별도 안내), 문구는 서버 message 를 그대로 쓴다.
 */
export class ApiError extends Error {
  constructor(readonly status: number, message: string, readonly code?: string) {
    super(message);
    this.name = 'ApiError';
  }

  get isAuth(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

export async function toApiError(r: Response): Promise<ApiError> {
  let msg = `HTTP ${r.status}`;
  let code: string | undefined;
  try {
    const b = await r.json();
    msg = b.message ?? b.error ?? msg;
    code = typeof b.error === 'string' ? b.error : undefined;
  } catch { /* 본문이 JSON 이 아닌 경우 */ }
  return new ApiError(r.status, msg, code);
}

// ─── 스토어 ────────────────────────────────────────────────────
const K_ACCESS = 'aitex.auth.access';
const K_REFRESH = 'aitex.auth.refresh';
const K_USER = 'aitex.auth.user';
export const AUTH_EVENT = 'aitex:auth';

function sget(k: string): string | null {
  try { return sessionStorage.getItem(k); } catch { return null; }
}
function sset(k: string, v: string | null): void {
  try {
    if (v) sessionStorage.setItem(k, v);
    else sessionStorage.removeItem(k);
  } catch { /* 시크릿 모드 등에서 접근이 막힐 수 있다 */ }
}
function emit(): void {
  window.dispatchEvent(new Event(AUTH_EVENT));
}

// ─── 로그인 세대 ───────────────────────────────────────────────
/**
 * 로그인 세대 (13회차 M29 · Codex 5차 N19 · N20) — 이 탭에서 「누가 로그인해 있는가」 가 바뀐 횟수. **이 파일 한 곳에서만** 센다.
 *
 * 오르는 곳은 둘뿐이다.
 *   ① 새 자격증명으로 로그인(login) — 같은 계정으로 다시 로그인해도 오른다(앞 세션의 「되돌리기」 가 살아나지 않게).
 *   ② 로그인을 지울 때(clearAuth) — 로그아웃 · 재발급 거절 · 탈퇴.
 * 토큰 교체(access 재발급 · 비밀번호 변경이 받는 새 토큰 쌍 · 내 정보 저장)는 **같은 세대**다. 12회차는 refresh 토큰 값으로 세대를
 * 판정해(session.ts) 앱 스스로의 토큰 교체(비밀번호 변경)까지 「다른 세션」 으로 보고 성공 처리 · 토스트를 건너뛰었다.
 *
 * 인증 API(재발급 · 내 정보 · 비밀번호 · 탈퇴 · 401 재시도)는 **시작 세대**를 잡고, 끝났을 때 세대가 바뀌었으면
 * 저장 · 삭제 · 재전송을 하지 않는다(StaleAuthError) — 앞 계정의 늦은 응답이 새 로그인을 지우거나 새 계정 정보에 앞 토큰을 섞었다(N20).
 * 화면 쪽 job.live() 는 이 부작용 뒤라 늦다 — 화면 상태용으로만 쓴다.
 */
let gen = 1;
export function authGen(): number { return gen; }

/** 시작한 로그인 세대가 끝난 뒤 도착한 인증 응답 — 저장하지 않고 버렸다. 호출부는 화면에 알리지 않는다 */
export class StaleAuthError extends Error {
  constructor() {
    super('로그인 상태가 바뀌어 앞 요청의 결과를 버렸습니다');
    this.name = 'StaleAuthError';
  }
}

/**
 * 시작 세대가 그대로일 때만 결과를 저장한다 — 바뀌었으면(실패도) StaleAuthError.
 * 14회차 Codex 6차 N20 잔존: 예전에는 검사한 뒤 값을 돌려주고 **호출부가 다시 깨어나서** 저장했다 — 그 사이(마이크로태스크 한 칸)
 * 새 로그인이 끝나면 앞 계정 정보 · 토큰이 새 세션 위에 저장되거나 새 로그인이 지워졌다. 이제 검사와 저장(commit)이 **같은 동기 구간**이다.
 */
async function inGen<T, R>(g: number, run: () => Promise<T>, commit: (v: T) => R): Promise<R> {
  let v: T;
  try {
    v = await run();
  } catch (e) {
    if (gen !== g) throw new StaleAuthError();
    throw e;
  }
  if (gen !== g) throw new StaleAuthError();
  return commit(v);
}

export function getAccessToken(): string | null { return sget(K_ACCESS); }
export function getRefreshToken(): string | null { return sget(K_REFRESH); }

export function getStoredUser(): AuthUser | null {
  const raw = sget(K_USER);
  if (!raw) return null;
  try { return JSON.parse(raw) as AuthUser; } catch { return null; }
}

export function setTokens(access: string, refresh?: string | null): void {
  sset(K_ACCESS, access);
  if (refresh !== undefined) sset(K_REFRESH, refresh);
  emit();
}

export function setStoredUser(u: AuthUser | null): void {
  sset(K_USER, u ? JSON.stringify(u) : null);
  emit();
}

/** 이 기기의 로그인을 지운다 — 지울 것이 있었으면 로그인 세대가 오른다(그 세션의 늦은 응답 · 토스트 · 되돌리기는 버려진다) */
export function clearAuth(): void {
  if (sget(K_ACCESS) || sget(K_REFRESH) || sget(K_USER)) gen += 1;
  sset(K_ACCESS, null);
  sset(K_REFRESH, null);
  sset(K_USER, null);
  emit();
}

export function isLoggedIn(): boolean {
  return Boolean(getAccessToken());
}

export function onAuthChange(fn: () => void): () => void {
  window.addEventListener(AUTH_EVENT, fn);
  return () => window.removeEventListener(AUTH_EVENT, fn);
}

// ─── 토큰 재발급 ───────────────────────────────────────────────
/**
 * 재발급 결과 — ok(새 access, 또는 그사이 교체된 지금 access) · rejected(서버가 거절 → 이 세대의 로그인을 지웠다) ·
 * network(닿지 않음) · stale(그사이 세대가 바뀌어 버림) · deferred(거절됐지만 비밀번호 변경이 진행 중 — 지우지 않고 그 결과를 기다린다, N26)
 */
type Refreshed = { kind: 'ok'; token: string } | { kind: 'rejected' | 'network' | 'stale' | 'deferred' };

/**
 * 진행 중인 재발급 — **세대 · refresh 토큰별**로 하나(N20: 앞 세션의 재발급 약속을 새 세션의 요청이 함께 기다리지 않게.
 * N26: 비밀번호 변경으로 토큰 쌍이 바뀐 뒤의 401 은 옛 refresh 로 보낸 재발급을 기다리지 않고 새 refresh 로 묻는다)
 */
let refreshing: { gen: number; rt: string | null; p: Promise<Refreshed> } | null = null;

/**
 * 진행 중인 토큰 쌍 교체(비밀번호 변경) — 14회차 Codex 6차 N26. 서버는 비밀번호를 바꾸면 token_version 을 올려 옛 refresh 를 모두 폐기한다.
 * 그래서 교체 요청과 겹친 옛 재발급은 ① 200(옛 판 access)로 와서 새 토큰을 덮거나 ② 401 로 와서 새 로그인을 통째로 지웠다.
 * 이제 재발급 응답은 **보낼 때의 refresh 가 아직 저장소에 있을 때만** 저장 · 삭제하고(아니면 지금 토큰으로 잇는다),
 * 교체가 진행 중일 때 거절되면 지우지 않고 교체 결과를 기다린다. 로그인 세대는 그대로다(토큰 교체는 같은 세션).
 */
let rotation: Promise<unknown> | null = null;

function refreshOnce(): Promise<Refreshed> {
  const g = gen;
  const rt = getRefreshToken();
  if (refreshing && refreshing.gen === g && refreshing.rt === rt) return refreshing.p;
  const p = doRefresh(g).finally(() => { if (refreshing?.p === p) refreshing = null; });
  refreshing = { gen: g, rt, p };
  return p;
}

/**
 * 재발급 응답이 왔을 때 — 이미 낡았는가. 세대가 바뀌었으면 stale, 같은 세대에서 refresh 가 바뀌었으면(비밀번호 변경이 새 쌍을 저장)
 * 이 응답은 버리고 **지금 access** 로 잇는다. 아니면 null(이 응답으로 저장 · 삭제해도 된다).
 */
function superseded(g: number, rt: string): Refreshed | null {
  if (gen !== g) return { kind: 'stale' };
  if (getRefreshToken() !== rt) {
    const cur = getAccessToken();
    return cur ? { kind: 'ok', token: cur } : { kind: 'stale' };
  }
  return null;
}

/**
 * access 토큰 1회 재발급. 동시에 여러 요청이 401 을 받아도 재발급은 (세대마다) 한 번만 한다.
 * 실패(폐기·정지)하면 스토어를 비우고 null 을 돌려준다. 그사이 로그아웃 · 다른 로그인이 있었으면 아무것도 저장 · 삭제하지 않고 null.
 */
export async function refresh(): Promise<string | null> {
  const r = await refreshOnce();
  return r.kind === 'ok' ? r.token : null;
}

async function doRefresh(g: number): Promise<Refreshed> {
  const rt = getRefreshToken();
  if (!rt) { clearAuth(); return { kind: 'rejected' }; }
  // 응답이 왔을 때 시작 세대가 아니면 — 앞 계정의 새 access 를 새 계정 정보에 붙이거나, 거절로 새 로그인을 지우지 않는다(N20).
  // 같은 세대여도 refresh 가 바뀌었으면(비밀번호 변경) 이 응답으로 새 토큰을 덮거나 지우지 않는다(N26) — superseded()
  // 거절 — 비밀번호 변경이 진행 중이면 서버가 이미 옛 refresh 를 폐기했을 수 있다. 지우지 않고 기다리게(deferred)
  const reject = (): Refreshed => {
    if (rotation) return { kind: 'deferred' };
    clearAuth();
    return { kind: 'rejected' };
  };
  try {
    if (getMode() === 'mock') {
      const t = (await loadAuthMock()).refresh(rt);
      const s = superseded(g, rt); if (s) return s;
      if (!t) return reject();
      setTokens(t);
      return { kind: 'ok', token: t };
    }
    const r = await fetch(`${BASE}${API}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    });
    const s1 = superseded(g, rt); if (s1) return s1;
    if (!r.ok) return reject();
    const b = await r.json();
    const s2 = superseded(g, rt); if (s2) return s2;
    if (!b.access_token) return reject();
    setTokens(b.access_token);
    return { kind: 'ok', token: b.access_token as string };
  } catch {
    // 네트워크 오류 — 호출부(authFetch)가 정리한다. 비밀번호 변경이 진행 중이면 거절과 같이 그 결과를 기다리게(deferred) —
    // 7차 N31: 「network」 로 돌아가 authFetch 가 곧바로 지웠고, 그 사이 변경이 저장한 같은 세대의 새 토큰까지 지웠다
    return superseded(g, rt) ?? (rotation ? { kind: 'deferred' } : { kind: 'network' });
  }
}

/**
 * Bearer 를 붙여 요청한다. 401 이면 재발급 1회 후 재시도하고, 그래도 안 되면
 * 스토어를 비운 뒤 **비로그인 상태로 한 번 더** 요청한다 — 검색처럼 공개된
 * 경로는 세션이 끊겨도 계속 동작해야 한다.
 *
 * path 는 BASE 뒤에 그대로 붙는다 (예: '/api/v1/auth/me').
 */
export async function authFetch(path: string, init: RequestInit = {}, opt: { rotating?: boolean } = {}): Promise<Response> {
  const run = (token: string | null) => fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const g = gen;
  let token = getAccessToken();
  /** 이 요청이 보낸 토큰 쌍 — 마지막에 로그인을 지우기 전 「아직 이 쌍인가」 를 본다(N31) */
  let sentRt = getRefreshToken();
  let r = await run(token);
  if (r.status !== 401 || !token) return r;
  // 13회차 N20: 보낸 세션이 이미 끝났다(로그아웃 · 다른 로그인) — 늦은 401 로 새 세션 토큰을 재발급 · 재전송하거나 새 로그인을 지우지 않는다
  if (gen !== g) return r;
  // N26: 보낸 뒤 같은 세션의 토큰이 이미 바뀌었다(다른 요청의 재발급 · 비밀번호 변경의 새 쌍) — 재발급 없이 지금 토큰으로 한 번 더
  const now = getAccessToken();
  if (now && now !== token) {
    token = now;
    sentRt = getRefreshToken();
    r = await run(now);
    if (r.status !== 401 || gen !== g) return r;
  }

  const nr = await refreshOnce();
  if (nr.kind === 'stale') return r;
  // 재발급 거절 — doRefresh 가 이미 이 세대의 로그인을 지웠다(세대가 올랐다). 공개 경로는 비로그인으로 한 번 더
  if (nr.kind === 'rejected') return run(null);
  if (gen !== g) return r;
  if (nr.kind === 'ok') return run(nr.token);
  // deferred(교체 중 거절 · 교체 중 네트워크 실패) · network — 진행 중인 토큰 쌍 교체(비밀번호 변경)가 있으면 그 결과를 기다린다(N26 · 7차 N31).
  // network 로 돌아온 뒤에 교체가 시작됐어도 기다린다. 이 요청이 그 교체 자신이면 기다리지 않고(서로 기다리게 된다)
  // 401 을 그대로 돌려 실패로 알린다 — 거절(deferred)과 같게. 예전에는 network 면 로그인을 지워 변경 실패가 StaleAuthError 로 묻혔다
  if (opt.rotating) return r;
  if (rotation) {
    await rotation.catch(() => undefined);
    if (gen !== g) return r;
  }
  // 7차 N31: 지우기 **직전**에 이 요청이 쓴 토큰이 아직 저장소에 있는지 다시 본다 — 같은 세대에 새 토큰(비밀번호 변경의 새 쌍 ·
  // 다른 요청의 재발급)이 저장됐으면 지우지 않고 그 토큰으로 잇는다. 검사와 지우기는 같은 동기 구간이다
  const cur = getAccessToken();
  if (cur && (cur !== token || getRefreshToken() !== sentRt)) return run(cur);
  // 교체가 없었거나 실패했고 토큰도 그대로다 — 이 세대의 로그인을 지우고 비로그인으로 한 번 더(공개 경로는 계속 동작)
  if (cur) clearAuth();
  return run(null);
}

/** JSON 요청 + 오류 → ApiError. */
export async function authJson<T>(path: string, init: RequestInit = {}, opt: { rotating?: boolean } = {}): Promise<T> {
  const r = await authFetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
  }, opt);
  if (!r.ok) throw await toApiError(r);
  if (r.status === 204) return undefined as T;
  return r.json() as Promise<T>;
}

// ─── 계정 API ──────────────────────────────────────────────────
async function useMock(): Promise<boolean> {
  await probe();
  return getMode() === 'mock';
}

export async function login(userId: string, password: string): Promise<LoginResponse> {
  let body: LoginResponse;
  if (await useMock()) {
    body = (await loadAuthMock()).login(userId, password);
  } else {
    const r = await fetch(`${BASE}${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, password }),
    });
    if (!r.ok) throw await toApiError(r);
    body = await r.json();
    if (!body.access_token) throw new ApiError(500, '응답에 access_token 이 없습니다');
  }
  // 새 자격증명으로 로그인 — 새 세대(같은 계정이어도). 앞 세션의 늦은 응답 · 토스트 · 되돌리기는 이제 버려진다
  gen += 1;
  sset(K_ACCESS, body.access_token);
  sset(K_REFRESH, body.refresh_token ?? null);
  sset(K_USER, JSON.stringify(body.user));
  emit();
  return body;
}

/**
 * 로그아웃 — 이 기기의 로그인을 먼저 지우고(세대가 오른다) 서버에 알린다. 지운 뒤의 세대를 돌려준다:
 * 호출부는 끝났을 때 세대가 그대로일 때만 「로그아웃되었습니다」 · 홈 이동을 한다(그사이 다시 로그인했으면 새 세션을 건드리지 않는다).
 * 서버 응답 뒤에는 아무것도 저장 · 삭제하지 않는다(N20 — 늦은 로그아웃 완료가 새 로그인 사용자 상태를 지웠다).
 */
export async function logout(): Promise<number> {
  const token = getAccessToken();
  clearAuth();
  const g = gen;
  if (!token) return g;
  if (await useMock()) { (await loadAuthMock()).logout(token); return g; }
  try {
    await fetch(`${BASE}${API}/auth/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch { /* 서버가 없어도 로컬 세션은 이미 지웠다 */ }
  return g;
}

/** GET /api/v1/auth/check-email?email=… → {available, reason?}. 입력 중 디바운스로 부른다. */
export async function checkEmail(email: string, signal?: AbortSignal): Promise<EmailCheck> {
  if (await useMock()) return (await loadAuthMock()).checkEmail(email);
  let r: Response;
  try {
    r = await fetch(`${BASE}${API}/auth/check-email?email=${encodeURIComponent(email)}`, {
      signal, headers: { Accept: 'application/json' },
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    return { available: null, reason: 'error' };
  }
  // 아직 배포되지 않은 서버(404·405), 또는 개발 서버가 index.html 을 돌려준 경우
  if (r.status === 404 || r.status === 405) return { available: null, reason: 'unsupported' };
  if (!r.headers.get('content-type')?.includes('application/json')) return { available: null, reason: 'unsupported' };
  const b = await r.json().catch(() => null) as { available?: unknown; reason?: unknown } | null;
  if (r.status === 422 || r.status === 400) return { available: false, reason: 'invalid' };
  if (!r.ok || !b) return { available: null, reason: 'error' };
  if (b.available === true) return { available: true };
  if (b.available === false) {
    // 서버는 'withdrawn'(탈퇴 계정의 이메일)을 쓴다 — 초안 계약의 'withdrawn_recent' 도 같은 뜻으로 받는다
    const reason = b.reason === 'invalid' ? 'invalid'
      : b.reason === 'withdrawn' || b.reason === 'withdrawn_recent' ? 'withdrawn' : 'taken';
    return { available: false, reason };
  }
  return { available: null, reason: 'unsupported' };
}

export async function register(input: RegisterInput): Promise<RegisterResult> {
  if (await useMock()) return (await loadAuthMock()).register(input);
  const r = await fetch(`${BASE}${API}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!r.ok) throw await toApiError(r);
  return r.json();
}

/** inGen 의 commit — 세대 검사와 같은 동기 구간에서 사용자 정보를 저장한다(N20) */
const commitUser = (u: AuthUser): AuthUser => { setStoredUser(u); return u; };

/** 내 정보 다시 받기 — 시작 세대가 끝났으면 저장하지 않는다(StaleAuthError, N20: 앞 계정 정보가 새 로그인 위에 저장됐다) */
export async function me(): Promise<AuthUser> {
  const g = gen;
  return inGen(g, async () => ((await useMock())
    ? (await loadAuthMock()).me(getAccessToken())
    : await authJson<AuthUser>(`${API}/auth/me`)), commitUser);
}

/** 내 정보 저장 — 같은 세대(토큰 교체 없음). 끝났을 때 세대가 바뀌었으면 저장하지 않는다(StaleAuthError) */
export async function updateMe(
  patch: Partial<Pick<AuthUser, 'display_name' | 'email' | 'organization' | 'phone'>>,
): Promise<AuthUser> {
  const g = gen;
  return inGen(g, async () => ((await useMock())
    ? (await loadAuthMock()).updateMe(getAccessToken(), patch)
    : await authJson<AuthUser>(`${API}/auth/me`, { method: 'PATCH', body: JSON.stringify(patch) })), commitUser);
}

/**
 * 비밀번호 변경. 서버는 기존 refresh 토큰을 모두 폐기하고 새 토큰 쌍을 돌려준다 —
 * 저장하지 않으면 다음 재발급에서 세션이 끊긴다. **토큰 교체일 뿐 같은 세대다**(13회차 M29 — 12회차는 이 교체를 「다른 세션」 으로 봐
 * 칸 비우기 · 성공 토스트 · 내 정보 갱신을 건너뛰었다). 끝났을 때 세대가 바뀌었으면(로그아웃 · 다른 로그인) 새 토큰을 저장하지 않는다.
 */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const g = gen;
  // N26: 이 요청이 끝날 때까지 「토큰 쌍 교체 중」 — 겹친 옛 재발급이 거절되면 로그인을 지우지 않고 이 결과를 기다린다(authFetch · doRefresh)
  const p = inGen(g, async () => ((await useMock())
    ? (await loadAuthMock()).changePassword(getAccessToken(), currentPassword, newPassword)
    : await authJson<{ ok: true; access_token?: string; refresh_token?: string }>(
      `${API}/auth/change-password`,
      { method: 'POST', body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }) },
      { rotating: true },
    )), (b) => {
    // 검사와 같은 동기 구간 — 그 사이 다른 로그인이 끝나 새 토큰을 덮는 일이 없다(N20)
    if (b.access_token) {
      sset(K_ACCESS, b.access_token);
      if (b.refresh_token) sset(K_REFRESH, b.refresh_token);
    }
    const u = getStoredUser();
    if (u) sset(K_USER, JSON.stringify({ ...u, must_change_password: false }));
    emit();
  });
  rotation = p;
  try { await p; } finally { if (rotation === p) rotation = null; }
}

/**
 * 탈퇴. 성공하면 이 기기의 로그인을 지운다(세대가 오른다) — 지우기 **바로 전**에 `beforeClear` 를 부른다: 호출부가 화면을 옮길 자리
 * (같은 그리기에 묶여 로그인 가드가 「로그인이 필요합니다」 로 먼저 보내지 않는다 — 13회차 M29).
 * 끝났을 때 세대가 이미 바뀌었으면(로그아웃 · 다른 로그인) 새 세션을 지우지 않고 StaleAuthError — 검사 · 이동 · 지우기가 같은 동기 구간(N20).
 */
export async function withdraw(password: string, beforeClear?: () => void): Promise<void> {
  const g = gen;
  await inGen(g, async () => {
    if (await useMock()) (await loadAuthMock()).withdraw(getAccessToken(), password);
    else await authJson<{ ok: true }>(`${API}/auth/withdraw`, { method: 'POST', body: JSON.stringify({ password }) });
  }, () => { try { beforeClear?.(); } finally { clearAuth(); } });
}
